import { useEffect, useState, useRef, useCallback } from 'react'
import { Marker, Popup, useMapEvents } from 'react-leaflet'
import { useDispatch, useSelector } from 'react-redux'
import { toPng } from 'html-to-image'
import markerImg from '../../assets/vertical-profile_marker.png'
import L, { LatLngTuple } from 'leaflet'
import './VerticalProfile.css'
import '../MapPopupTable/MapPopupTable.css'
import { timeouts } from '../../services/RequestTimeouts'
import useErrorStatus from '../../hooks/UseErrorStatus'
import { deselectMenuItemById } from '../../menus/SideNavigation/SideNavigationSlice'
import { VerticalProfileAPI } from '../../services/SurfaceObsAPI'
import { RootState } from '../../store'
import React from 'react';
import downloadIcon from "../../assets/icons/popup-download.svg"

interface Geometry {
  type: 'Point' | 'Polygon' | 'LineString';
  coordinates: [number, number];
}

interface VerticalProfileInterface {
  type: 'Feature';
  geometry: Geometry;
  properties: Record<string, any>;
}

const VerticalProfile = () => {
  const [verticalProfile, setWeather] = useState<Array<VerticalProfileInterface>>([])
  const [showVerticalProfile, setverticalProfileMarker] = useState(false)
  const location = useSelector((state) => state.user.defaultLocation)
  const elementRef = useRef<HTMLDivElement>(null)
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [imageURL, setImageURL] = useState<string>('')
  const [imagePos, setImagePos] = useState<LatLngTuple >([0, 0])
  const [timedOut, setTimedOut] = useState<boolean>(false)
  const [profilerDataView, setProfilerView] = useState<boolean>(true)
  const controller = new AbortController()
  const errorStatus = useErrorStatus()
  const dispatch = useDispatch()
  const timeoutId = setTimeout(() => {
    controller.abort('API call timed out')
    clearTimeout(timeoutId)
    setTimedOut(true)
  }, timeouts.verticalProfile)
  const currentLocation = useSelector(
    (state: RootState) => state.user.defaultLocation
  )

  // Weatherobs API call
  const getData = async () => {
    try {   
      const data = await VerticalProfileAPI(currentLocation)
      if (data.message == 'Not Authorized.') {
        errorStatus.addMessage('User not authorized', 400, 'error', true)
      } else if ( data?.features.length > 0) {
        errorStatus.addMessage(
          'Vertical profile data availabe in the current location',
          400,
          'success',
          false
        )
        setWeather(data.features)
        setverticalProfileMarker(true)
      } else {
        errorStatus.addMessage(
          'No vertical profile data availabe in the current location',
          400,
          'error',
          true
        )
        // deselect vertical profile menu
        dispatch(deselectMenuItemById('vertical-profile'))
      }
    } catch (error) {
      if (error.message === 'The user aborted a request.') {
        // set by abort controller
        dispatch(deselectMenuItemById('vertical-profile'))
        errorStatus.addMessage(
          'Vertical profile request timed out. Please try again.',
          400,
          'error',
          true
        )
      } else {
        errorStatus.addMessage(error.message, 400, 'error', true)
      }
    }
  }
  // Trigger when we load the page
  useEffect(() => {
    getData()
    setProfilerView(true)
  }, [location])

  const color_maping = (key: string, val: any, units: any) => {
    const base_string = 'text-base px-2 whitespace-nowrap text-center font-medium '
    let color_string = 'bg-[#F3F4F6] text-[#4B5563]'
    if (isNaN(val)) {
      return base_string + color_string
    }
    let low_temp
    let high_temp
    if (key == 'temp') {
      if(units == "F") val = (val - 32) / 1.8;
       
      if (val >= 2) low_temp = 'green'
      else if (val < 2 && val >= -4) low_temp = 'yellow'
      else low_temp = 'red'

      if (val <= 32) high_temp = 'green'
      else if (val > 32 && val < 34) high_temp = 'yellow'
      else high_temp = 'red'

      if (low_temp === 'green' && high_temp === 'green') {
        color_string = 'bg-[#DFFCE6] text-[#346F3E]'
      }
      if (low_temp === 'yellow' || high_temp === 'yellow') {
        color_string = 'bg-[#FCF9C1] text-[#895B1A]'
      }
      if (low_temp === 'red' || high_temp === 'red') {
        color_string = 'bg-[#FFE5E5] text-[#A03333]'
      }
    } else if (key == 'wind') {
      if (val <= 8) color_string = 'bg-[#DFFCE6] text-[#346F3E]'
      else if (val > 8 && val <= 14) color_string = 'bg-[#FCF9C1] text-[#895B1A]'
      else color_string = 'bg-[#FFE5E5] text-[#A03333]'
    }
    return base_string + color_string
  }

  const htmlToImageConvert = useCallback((skewTImageLink) => {
    console.log(skewTImageLink);
    
    if (elementRef.current === null) {
      return
    }
    if(skewTImageLink){
      const imageLink = document.createElement('a')
      let fileName = "VP-SkewTImage-" + Date.now() + '.png'
      imageLink.download = fileName
      imageLink.href = skewTImageLink
      imageLink.click()
      errorStatus.addMessage(
        'PNG exported: ' + fileName,
        400,
        'success',
        false,
        10 * 1000
      )
    }
    toPng(elementRef.current, { cacheBust: true })
      .then((dataUrl) => {
        const link = document.createElement('a')
        const fileName = 'VP-ProfilerData-' + Date.now() + '.png'
        link.download = fileName
        link.href = dataUrl
        link.click()
        errorStatus.addMessage(
          'PNG exported: ' + fileName,
          400,
          'success',
          false
        )
      })
      .catch((err) => {
        console.log(err)
      })
  }, [elementRef])

  const markerIcon = new L.Icon({
    iconUrl: markerImg,
    iconSize: [24, 36],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12],
  })

  const downloadCSVFile = async (url : string) => {
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `raw_csv_file.csv`)
    document.body.appendChild(link)
    link.click()
  }

  const map = useMapEvents({
    popupclose: () => {
      setModalActive(false)
    },
  })

  function getColumns(stationData: VerticalProfileInterface, addExtraCol :boolean): Array<string> {
    const customOrdering = ["air_pressure", "wind_speed", "wind_direction", "temperature", "dew_point_temperature", "relative_humidity"]
    let columns = new Set<string>()
    for(let key of customOrdering){
      for(let height of Object.keys(stationData)){        
        if(key in stationData[height]){ 
          columns.add(key)          
        }
      } 
    }
    var flteredArray = Array.from(columns)
    if (addExtraCol) return ["HT"].concat(flteredArray)
    return flteredArray
  }

  function getHeights(stationData: VerticalProfileInterface): Array<string> {
    let heights = []
    for(let key of Object.keys(stationData)) heights.push(key)
    return heights.sort((a,b) => Number(b) - Number(a))
  }

  function formatString(metaKeys: string): string {
    return metaKeys.split("_").join(" ")
  }

  const keyMap : Record<string, string> = {
    //"dew_point_temperature" : "temp",
    "temperature" : "temp",
    "wind_speed" : "wind",
    "meters" : "m",
    "feets" : "ft"
  }

  const unitMap = {
    "DEG" : "°",
    "F" : " °F",
    "C" : " °C",
    "KTS" : " KTS",
    "%" : " %",
    "hPa" : " hPa"
  }
  return (
    <>
      {showVerticalProfile &&
        verticalProfile.map((station, index) => (
          <Marker key={index} position={[station.geometry.coordinates[1], station.geometry.coordinates[0]]} icon={markerIcon} title={'Vertical Profile'}>
            <Popup autoClose={false} className="vertical-profile-popup">
              <div className="flex flex-col gap-2 h-full justify-center min-w-[40vh]">
                {/* Header code comprising of the download buttons*/}
                  <>
                    <div className='flex gap-2'>
                      <div className='flex grow gap-2 justify-start items-center'>
                        <div className="header">
                          Vertical Profile
                        </div>
                        <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9] whitespace-nowrap">
                          {station.properties["sensor_type"]}
                        </div>
                        {
                          station.properties["station_id"] &&
                          <div className="font-semibold  border shadow-sm p-1 rounded-full bg-[#F1F5F9] whitespace-nowrap">
                            {station.properties["station_id"]}
                          </div>
                        }
                      </div>
                      <div className='flex grow gap-2 justify-end items-center '>
                        {
                          station.properties.link_csv &&
                          <div className="p-2 border rounded-lg divide-y shadow-sm bg-[#FFFFFF] valueStyling hover:bg-[#F1F5F9]">
                            <button className='flex items-center gap-1 whitespace-nowrap' type="button" onClick={async (event) => { await downloadCSVFile(station.properties.link_csv); }}>
                            <img src={downloadIcon} alt="Icon" className='sm:hidden md:inline-block h-4'/> Download CSV
                            </button>
                          </div>
                        }
                        <div className="p-2 border rounded-lg divide-y shadow-sm bg-[#FFFFFF] valueStyling hover:bg-[#F1F5F9]">
                          <button className='flex items-center gap-1 whitespace-nowrap' type="button" onClick={() => htmlToImageConvert(station.properties.link_image)}>
                          <img src={downloadIcon} alt="Icon" className='sm:hidden md:inline-block h-4'/> Export
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="labelStyling">
                        {station.properties["timestamp"]}
                    </div>
                    <hr/>
                  </>

                {/*Tab section if we have an skewt image available*/}
                  <>
                    {
                      station.properties.link_image && 
                      <>
                        <div className='flex'>
                          <button className={`tabStyling valueStyling shadow-sm hover:bg-[#FFFFFF] rounded-l-lg ${profilerDataView && 'bg-[#FFFFFF] border-2 border-gray-500'}`} onClick={() => {setProfilerView(true);}}>Profiler Data</button>
                          <button className={`tabStyling valueStyling shadow-sm hover:bg-[#FFFFFF] rounded-r-lg ${!profilerDataView && 'bg-[#FFFFFF] border-2 border-gray-500'}`} onClick={() => {setProfilerView(false);}}>Skew-T Diagram</button>
                        </div>
                        <hr/>
                      </>
                    }
                  </>
                
                {/*Vertical profile and skew-t image data*/}
                  <>
                    {
                      profilerDataView ? 
                      <div className="rounded-lg border-2 border-gray-300 shadow-sm min-h-0 max-h-[60vh] overflow-y-auto" >
                        {/* Belowe we have a table but we are using div tag because ref={elementRef} doesn't work in table tag*/}
                          <table className="min-w-full text-sm text-left text-gray-500 divide divide-y divide-gray-500"  ref={elementRef}>
                            <thead className="bg-gray-100 text-black sticky top-0">
                              <tr className='divide-x divide-gray-500  bg-[#FFFFFF]'>
                                {
                                  getColumns(station.properties.data, true).map((columNames, index) => (
                                    <th scope="col" className="p-2 uppercase text-center font-semibold text-base">
                                      {formatString(columNames)}
                                    </th>
                                  ))
                                }
                              </tr>
                            </thead>
                            <tbody className='divide-y divide-gray-500 h-max'>
                              {
                                getHeights(station.properties.data).map((heightKey, index) => (
                                  <tr className="text-black divide-x divide-gray-500">
                                    <th scope="row" className="bg-gray-100 px-6 py-2 font-semibold  whitespace-nowrap text-base">
                                      {Math.round(Number(heightKey))} {keyMap[station.properties.altitude_units]}
                                    </th>
                                    {
                                      getColumns(station.properties.data, false).map((param) => (
                                        <td className={color_maping(keyMap[param] || "default", station.properties.data[heightKey][param]?.value, station.properties.data[heightKey][param]?.units )}>
                                          {
                                            station.properties.data[heightKey][param]?.value === undefined ||
                                              isNaN(station.properties.data[heightKey][param]?.value) ?
                                              "-" : `${Math.round(Number(station.properties.data[heightKey][param]?.value))}${unitMap[station.properties.data[heightKey][param]?.units] ?? station.properties.data[heightKey][param]?.units}`
                                          }
                                        </td>
                                      ))
                                    }
                                  </tr>
                                ))
                              }
                            </tbody>
                          </table>
                      </div>
                      :
                      <div>
                        {/* This section shows the skewt image and opens a popup on click */}
                        {
                          station.properties.link_image &&
                          <div className="cursor-pointer border border-slate-500 w-[50vh]" onClick={() => {
                            setImagePos([station.geometry.coordinates[1], station.geometry.coordinates[0]])
                            setImageURL(station.properties.link_image)
                            setModalActive(true)
                          }}>
                            <img src={station.properties.link_image}></img>
                          </div>
                        }
                      </div>
                    }
                  </>
              </div>              
            </Popup>
          </Marker>
        )
        )
      }
      {
        modalActive &&
        <Popup className="popup-skewt-image" position={imagePos} autoClose={false} >
          <img src={imageURL} alt="skewt diagram" className="skewt-image" />
        </Popup>
      }
    </>
  )
}
export default VerticalProfile
